import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Tabs, type TabItem } from "./Tabs";

interface WorkspaceTabsProps<T extends string> {
  tabs: TabItem<T>[];
  /**
   * Active tab value, normally derived from the route by the caller.
   * If the value does not appear in `tabs`, the workspace will redirect to `fallback`.
   */
  value: T;
  /**
   * Tab to redirect to when the URL points at an unknown tab value. The first tab is used
   * when omitted.
   */
  fallback?: T;
  /**
   * Base path of the workspace (e.g. "/config"). Tabs navigate via `${basePath}/${value}`
   * unless `useQueryParam` is set, in which case `?tab=` query state is used instead.
   */
  basePath?: string;
  /**
   * When true, the active tab is encoded into a query parameter (default ``?tab=``)
   * rather than a path segment. Use this when the workspace lives at a fixed path
   * such as ``/foo?tab=bar``. Defaults to false (path mode).
   */
  useQueryParam?: boolean;
  /**
   * Name of the query parameter used when ``useQueryParam`` is true. Defaults to
   * ``"tab"``; override (e.g. ``"pivot"``) when nesting workspace tabs inside
   * another workspace that already owns ``?tab=``.
   */
  queryParamName?: string;
  ariaLabel?: string;
}

export function WorkspaceTabs<T extends string>({
  tabs,
  value,
  fallback,
  basePath,
  useQueryParam = false,
  queryParamName = "tab",
  ariaLabel,
}: WorkspaceTabsProps<T>) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const known = tabs.some((t) => t.value === value);
  const defaultTab = fallback ?? tabs[0]?.value;

  useEffect(() => {
    if (known || !defaultTab) return;
    if (useQueryParam) {
      const next = new URLSearchParams(searchParams);
      next.set(queryParamName, defaultTab);
      setSearchParams(next, { replace: true });
    } else if (basePath) {
      navigate(`${basePath}/${defaultTab}`, { replace: true });
    }
  }, [
    known,
    defaultTab,
    useQueryParam,
    queryParamName,
    basePath,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  const handleChange = (next: T) => {
    if (next === value) return;
    if (useQueryParam) {
      const params = new URLSearchParams(searchParams);
      params.set(queryParamName, next);
      setSearchParams(params);
    } else if (basePath) {
      navigate(`${basePath}/${next}`);
    }
  };

  return (
    <Tabs
      tabs={tabs}
      value={known ? value : defaultTab ?? value}
      onChange={handleChange}
      ariaLabel={ariaLabel}
    />
  );
}
